export const publicEnv = {
  supabaseUrl: process.env.NEXT_PUBLIC_SUPABASE_URL!,
  supabaseAnonKey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
  appUrl: process.env.NEXT_PUBLIC_APP_URL!,
  posthogToken: process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN!,
  posthogHost: process.env.NEXT_PUBLIC_POSTHOG_HOST!,
  // Cloudflare Turnstile site key, guarding the OTP request. Deliberately `?? ''` rather
  // than `!` like its siblings: an env without the key must soft-disable the widget (login
  // still works) instead of rendering a broken challenge. The matching secret lives in Supabase
  // Auth (GoTrue performs the OTP siteverify call) AND in this app's own env as
  // `serverEnv.turnstileSecretKey`, which `verifyTurnstile` uses for the free-scan routes.
  turnstileSiteKey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '',
  // Crisp chat widget website ID. Deliberately `?? ''` rather than `!` like its siblings: an
  // env without the ID must soft-disable the widget (the Crisp mount component renders
  // nothing) instead of crashing the app at import time.
  crispWebsiteId: process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID ?? '',
}

/**
 * True when running in a production Node/Edge environment.
 * Use this instead of `process.env.NODE_ENV === 'production'` directly.
 * Kept as a top-level constant (not on publicEnv/serverEnv) because it reads
 * NODE_ENV, not a NEXT_PUBLIC_* var — mixing those contracts would be misleading.
 */
export const IS_PRODUCTION = process.env.NODE_ENV === 'production'

/**
 * True ONLY on Vercel's Production environment (`VERCEL_ENV === 'production'`).
 * NOT interchangeable with `IS_PRODUCTION` — Vercel sets `NODE_ENV=production`
 * for BOTH preview and production builds, so `IS_PRODUCTION` is true on a
 * preview deploy too. `VERCEL_ENV` is the only signal that actually
 * distinguishes them (`'production' | 'preview' | 'development'`, unset
 * outside Vercel). Use this to hard-disable anything that must never run
 * against a preview deploy — e.g. the subscription-reconciliation cron, which
 * reads/writes the PRODUCTION database while `src/lib/polar/client.ts` only
 * switches Polar to sandbox when `NODE_ENV === 'development'` (never true on
 * a Vercel preview build), so an unguarded preview run would reconcile prod
 * subscription rows against Polar's LIVE API.
 */
export const IS_VERCEL_PRODUCTION = process.env.VERCEL_ENV === 'production'

export const serverEnv = {
  get supabaseServiceRoleKey() {
    const value = process.env.SUPABASE_SERVICE_ROLE_KEY
    if (!value) throw new Error('Missing SUPABASE_SERVICE_ROLE_KEY')
    return value
  },
  get googleGenerativeAiApiKey() {
    const value = process.env.GOOGLE_GENERATIVE_AI_API_KEY
    if (!value) throw new Error('Missing GOOGLE_GENERATIVE_AI_API_KEY')
    return value
  },
  // Google Cloud OAuth 2.0 Web application client, used for GSC/GA4 connections
  // (`src/lib/integrations/google/**`). Distinct from
  // `SUPABASE_AUTH_EXTERNAL_GOOGLE_CLIENT_ID`/`_SECRET`, which is Supabase Auth's
  // "Sign in with Google" client in the same Google Cloud project.
  get googleOauthClientId() {
    const value = process.env.GOOGLE_OAUTH_CLIENT_ID
    if (!value) throw new Error('Missing GOOGLE_OAUTH_CLIENT_ID')
    return value
  },
  get googleOauthClientSecret() {
    const value = process.env.GOOGLE_OAUTH_CLIENT_SECRET
    if (!value) throw new Error('Missing GOOGLE_OAUTH_CLIENT_SECRET')
    return value
  },
  get openaiApiKey() {
    const value = process.env.OPENAI_API_KEY
    if (!value) throw new Error('Missing OPENAI_API_KEY')
    return value
  },
  get perplexityApiKey() {
    const value = process.env.PERPLEXITY_API_KEY
    if (!value) throw new Error('Missing PERPLEXITY_API_KEY')
    return value
  },
  get polarAccessToken() {
    const value = process.env.POLAR_ACCESS_TOKEN
    if (!value) throw new Error('Missing POLAR_ACCESS_TOKEN')
    return value
  },
  get polarWebhookSecret() {
    const value = process.env.POLAR_WEBHOOK_SECRET
    if (!value) throw new Error('Missing POLAR_WEBHOOK_SECRET')
    return value
  },
  get polarOrganizationId() {
    const value = process.env.POLAR_ORGANIZATION_ID
    if (!value) throw new Error('Missing POLAR_ORGANIZATION_ID')
    return value
  },
  get polarProductStarter() {
    const value = process.env.POLAR_PRODUCT_STARTER
    if (!value) throw new Error('Missing POLAR_PRODUCT_STARTER')
    return value
  },
  get polarProductGrowth() {
    const value = process.env.POLAR_PRODUCT_GROWTH
    if (!value) throw new Error('Missing POLAR_PRODUCT_GROWTH')
    return value
  },
  get polarProductPro() {
    const value = process.env.POLAR_PRODUCT_PRO
    if (!value) throw new Error('Missing POLAR_PRODUCT_PRO')
    return value
  },
  get polarProductStarterAnnual() {
    const value = process.env.POLAR_PRODUCT_STARTER_ANNUAL
    if (!value) throw new Error('Missing POLAR_PRODUCT_STARTER_ANNUAL')
    return value
  },
  get polarProductGrowthAnnual() {
    const value = process.env.POLAR_PRODUCT_GROWTH_ANNUAL
    if (!value) throw new Error('Missing POLAR_PRODUCT_GROWTH_ANNUAL')
    return value
  },
  get polarProductProAnnual() {
    const value = process.env.POLAR_PRODUCT_PRO_ANNUAL
    if (!value) throw new Error('Missing POLAR_PRODUCT_PRO_ANNUAL')
    return value
  },
  get polarProductAgency() {
    const value = process.env.POLAR_PRODUCT_AGENCY
    if (!value) throw new Error('Missing POLAR_PRODUCT_AGENCY')
    return value
  },
  get polarProductAgencyAnnual() {
    const value = process.env.POLAR_PRODUCT_AGENCY_ANNUAL
    if (!value) throw new Error('Missing POLAR_PRODUCT_AGENCY_ANNUAL')
    return value
  },
  get polarProductAddonPrompts() {
    const value = process.env.POLAR_PRODUCT_ADDON_PROMPTS
    if (!value) throw new Error('Missing POLAR_PRODUCT_ADDON_PROMPTS')
    return value
  },
  get polarProductAddonEngineStarter() {
    const value = process.env.POLAR_PRODUCT_ADDON_ENGINE_STARTER
    if (!value) throw new Error('Missing POLAR_PRODUCT_ADDON_ENGINE_STARTER')
    return value
  },
  get polarProductAddonEngineGrowth() {
    const value = process.env.POLAR_PRODUCT_ADDON_ENGINE_GROWTH
    if (!value) throw new Error('Missing POLAR_PRODUCT_ADDON_ENGINE_GROWTH')
    return value
  },
  get polarProductAddonEnginePro() {
    const value = process.env.POLAR_PRODUCT_ADDON_ENGINE_PRO
    if (!value) throw new Error('Missing POLAR_PRODUCT_ADDON_ENGINE_PRO')
    return value
  },
  get polarProductAddonEngineAgency() {
    const value = process.env.POLAR_PRODUCT_ADDON_ENGINE_AGENCY
    if (!value) throw new Error('Missing POLAR_PRODUCT_ADDON_ENGINE_AGENCY')
    return value
  },
  get cronSecret() {
    const value = process.env.CRON_SECRET
    if (!value) throw new Error('Missing CRON_SECRET')
    return value
  },
  get resendApiKey() {
    const value = process.env.RESEND_API_KEY
    if (!value) throw new Error('Missing RESEND_API_KEY')
    return value
  },
  get unsubscribeSecret() {
    const value = process.env.UNSUBSCRIBE_SECRET
    if (!value) throw new Error('Missing UNSUBSCRIBE_SECRET')
    return value
  },
  get emailFrom() {
    const raw = process.env.EMAIL_FROM ?? 'hello@ranksurf.com'
    // Always ensure "Display Name <email>" format so the sender shows as "RankSurf" not "hello"
    if (raw.includes('<')) return raw
    return `RankSurf <${raw}>`
  },
  get polarEarlyAdopterDiscountId() {
    const value = process.env.POLAR_EARLY_ADOPTER_DISCOUNT_ID
    if (!value) throw new Error('Missing POLAR_EARLY_ADOPTER_DISCOUNT_ID')
    return value
  },
  // Optional — free tier works without a key
  get jinaApiKey(): string {
    return process.env.JINA_API_KEY ?? ''
  },
  // Optional — Ed25519 private key (JWK, JSON-encoded) used to sign outbound crawl
  // requests under Web Bot Auth (RFC 9421 / Cloudflare's http-message-signatures-
  // directory). Absent in dev/CI and any environment that hasn't generated a key yet.
  // `src/lib/crawl/web-bot-auth.ts` treats a missing OR malformed value as "crawl
  // unsigned" — never throws — since crawling must keep working with no key
  // configured. Generate with `pnpm tsx scripts/generate-web-bot-auth-key.ts`.
  get webBotAuthPrivateKeyJwk(): string | undefined {
    return process.env.WEB_BOT_AUTH_PRIVATE_KEY_JWK
  },
  get triggerSecretKey() {
    const value = process.env.TRIGGER_SECRET_KEY
    if (!value) throw new Error('Missing TRIGGER_SECRET_KEY')
    return value
  },
  // Vercel's own commit SHA for the currently-serving deployment — a system env var Vercel
  // auto-populates on every build (Production and Preview), never set locally or in CI. Compared
  // against Trigger.dev's own deployed commit by `trigger-deploy-drift` (R3.17c ISSUE-25) to catch
  // a Trigger.dev deploy that failed silently (e.g. against the 10-schedule plan cap) while Vercel
  // kept shipping normally. Optional: absent outside Vercel; the comparison route itself only runs
  // when `IS_VERCEL_PRODUCTION`.
  get vercelGitCommitSha(): string | undefined {
    return process.env.VERCEL_GIT_COMMIT_SHA
  },
  // Optional: comma-separated list of admin emails. Empty = no restriction (dev mode).
  get adminEmails(): string[] {
    return (process.env['ADMIN_EMAILS'] ?? '')
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean)
  },
  // Optional: set to '0' to silence perf logs in dev; set to '1' to enable in prod.
  get perfLog(): string | undefined {
    return process.env.PERF_LOG
  },
  get indexNowKey() {
    const value = process.env.INDEXNOW_KEY
    if (!value) throw new Error('Missing INDEXNOW_KEY')
    return value
  },
  // Optional — local-only outreach pipeline
  get notionToken(): string {
    return process.env.NOTION_TOKEN ?? ''
  },
  get notionDatabaseId(): string {
    return process.env.NOTION_DATABASE_ID ?? ''
  },
  // Cloudflare Turnstile secret, used by `verifyTurnstile` (`src/lib/tools/turnstile.ts`) for the
  // free-scan routes. Deliberately NOT a throwing getter like the required keys above: the caller
  // refuses the request with the user-facing "We couldn't confirm you're human" message and
  // reports the missing secret to Sentry, instead of a 500 with no remedy.
  get turnstileSecretKey(): string {
    return process.env.TURNSTILE_SECRET_KEY ?? ''
  },
  // Svix signing secret (`whsec_…`) of the Resend webhook endpoint `POST /api/webhooks/resend`.
  // Deliberately `?? ''` rather than a throwing getter: the route answers 500 and processes nothing
  // when it is unset, and reports that to Sentry, so a missing secret is loud but never a bypass.
  get resendWebhookSecret(): string {
    return process.env.RESEND_WEBHOOK_SECRET ?? ''
  },
  get freeToolRateLimitSalt(): string {
    return process.env.FREE_TOOL_RATE_LIMIT_SALT ?? 'dev-salt-change-in-prod'
  },
  // Max free-tool calls (all tools combined) allowed globally per 24h.
  // Prevents distributed / botnet DoS even when per-IP limits are respected.
  // Default: 500. Override with FREE_TOOL_DAILY_GLOBAL_CAP env var.
  get freeToolDailyGlobalCap(): number {
    const raw = process.env.FREE_TOOL_DAILY_GLOBAL_CAP
    if (!raw) return 500
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 500
  },
  get dataforseoApiLogin() {
    const value = process.env.DATAFORSEO_API_LOGIN
    if (!value) throw new Error('Missing DATAFORSEO_API_LOGIN')
    return value
  },
  get dataforseoApiPassword() {
    const value = process.env.DATAFORSEO_API_PASSWORD
    if (!value) throw new Error('Missing DATAFORSEO_API_PASSWORD')
    return value
  },
  // Optional — CrUX (Chrome UX Report) API key, free tier
  // (`src/lib/providers/crux/client.ts`). Absent in dev/CI until one is generated. Deliberately
  // NOT a throwing getter like the required keys above: `queryCruxRecord` itself throws a typed
  // `CruxKeyMissingError` before any fetch when this is undefined (REL-03 — CrUX must fail the
  // whole batch closed rather than one call silently no-op), so the fail-closed decision belongs
  // to that call site, not to this getter.
  get cruxApiKey(): string | undefined {
    return process.env.CRUX_API_KEY
  },
  // Optional — YouTube monitoring (Phase 3)
  get youtubeApiKey(): string {
    return process.env.YOUTUBE_API_KEY ?? ''
  },
  get supadataApiKey(): string {
    return process.env.SUPADATA_API_KEY ?? ''
  },
  // Kill switch for the free-scan-engine-backfill Trigger.dev task. Default-enabled —
  // set to the literal string 'false' in Vercel env + redeploy to disable without touching
  // the adoption path itself.
  get freeScanBackfillEnabled(): boolean {
    return process.env.FREE_SCAN_BACKFILL_ENABLED !== 'false'
  },
  // Per-provider dispatch kill switches (growth-engine-r2g-loop · G8; `BRIEF.md:141`'s promised
  // "a kill switch (env var or DB flag) stops new dispatches without a deploy";
  // `PRICING.md:479` cost-control contract item 4, "give every provider/feature a kill switch").
  // All three mirror `freeScanBackfillEnabled`'s own shape exactly: default-ENABLED (an unset var
  // must never accidentally disable a production dispatch that spends real money or that other
  // features depend on completing), disabled only by setting the literal string 'false' in the
  // deploy env — no redeploy required, checked fresh at dispatch time.
  get dataforseoDispatchEnabled(): boolean {
    return process.env.DATAFORSEO_DISPATCH_ENABLED !== 'false'
  },
  get gscDispatchEnabled(): boolean {
    return process.env.GSC_DISPATCH_ENABLED !== 'false'
  },
  get ga4DispatchEnabled(): boolean {
    return process.env.GA4_DISPATCH_ENABLED !== 'false'
  },
}

/**
 * Returns true if the given email belongs to an admin.
 *
 * Fails CLOSED in production: if ADMIN_EMAILS is empty/unset, no one is an admin
 * (a missing env var must never silently grant every authenticated user admin
 * access to /api/admin/*). In dev/test an empty list keeps the convenient
 * open mode so local work isn't blocked.
 */
export function isAdmin(email: string | null | undefined): boolean {
  const emails = serverEnv.adminEmails
  if (emails.length === 0) return process.env.NODE_ENV !== 'production'
  return emails.includes(email ?? '')
}

/**
 * Returns true if the given email may read ANOTHER user's project via the
 * read-only `pid`-cookie impersonation path.
 *
 * Deliberately NOT `isAdmin()`. `isAdmin()` fails OPEN outside production (an
 * empty ADMIN_EMAILS makes every authenticated user an admin locally, which is
 * a convenience for /api/admin/* work). Inheriting that here would mean every
 * dev user could read every project by editing a cookie, AND — because the
 * proxy stops normalizing a foreign `pid` and starts 403-ing writes for admin
 * sessions — it would silently change behaviour for ordinary non-admin users
 * in dev. Cross-tenant reads must never be granted by an UNSET env var.
 *
 * Fails CLOSED in every environment: an empty/unset ADMIN_EMAILS means nobody
 * can impersonate. To use impersonation locally, set ADMIN_EMAILS explicitly.
 *
 * See docs/tasks/admin-project-impersonation/plan.md.
 */
export function canImpersonateProjects(email: string | null | undefined): boolean {
  const emails = serverEnv.adminEmails
  if (emails.length === 0) return false
  return emails.includes(email ?? '')
}
